import React, { useCallback, useState } from 'react';
import { useMutation } from 'react-query';
import { Trans } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { isAfter } from 'date-fns';
import { format } from 'date-fns-tz';

import { createQueriedURL, routeReplacer } from '~/Helpers/String.helper';
import { EVENT_SERVICE_URLS } from '~/Constants/Urls.constants';
import { ToastVariant, useDateFns, useProvider, useToastMessage } from '~/Hooks';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { extractErrorMessage } from '~/Helpers/Error.helper';
import { EventGroup } from '~/Models/DTO/EventGroup.model';
import { IEventSeries } from '~/Models/DTO/EventSeries.model';
import { IEndEventGroupConfirmationOptions } from '~/Screens/EventGroupList/types';
import { IEvent } from '~/Models/DTO/Event.model';
import { REQUEST_ACTIONS, STATUS } from '~/Constants/Actions.constants';

export const useEndEventGroup = ({
  onSuccess,
  shouldCleanNextEvent,
  shouldCleanEventGroupStore,
}: {
  onSuccess?: () => void;
  shouldCleanNextEvent?: boolean;
  shouldCleanEventGroupStore?: boolean;
} = {}) => {
  const { showToast } = useToastMessage();
  const { currentProvider } = useProvider();
  const dispatch = useDispatch();
  const { convertToProviderTimezone } = useDateFns();
  const [endEventGroupOptions, setEndEventGroupOptions] = useState<EventGroup | void>();

  const endEventGroup = useMutation(
    async ({
      eventGroup: { id },
      options: { endDate },
    }: {
      eventGroup: EventGroup;
      options: IEndEventGroupConfirmationOptions;
    }) => {
      const eventSeries = await requestReactQuery<IEventSeries[]>({
        url: routeReplacer(EVENT_SERVICE_URLS.EVENT_SERIES_GET, {
          eventGroupId: id,
          providerId: currentProvider?.uuid ?? '',
        }),
        config: { method: 'GET' },
        apiVersion: '2',
      });

      await Promise.all(
        eventSeries
          .filter(
            (singleEventSeries) =>
              !singleEventSeries.endDate || isAfter(Date.parse(singleEventSeries.endDate), endDate),
          )
          .map(({ id = '', ...singleEventSeries }) =>
            requestReactQuery<IEventSeries>({
              url: routeReplacer(EVENT_SERVICE_URLS.EVENT_SERIES_DETAIL, { id }),
              data: {
                ...singleEventSeries,
                endDate: format(new Date(endDate), 'yyyy-MM-dd'),
              },
              config: { method: 'PUT' },
            }),
          ),
      );
    },
    {
      onSuccess,
      onError: (errorObj: IErrorResponse) => {
        const message = extractErrorMessage(errorObj) as any;
        showToast(
          'End event group error',
          <Trans i18nKey={message?.key} defaults={message?.defaultMessage || message} />,
          ToastVariant.ERROR,
        );
      },
    },
  );

  const updateEventGroupStore = useMutation(
    async ({ eventGroup: { id } }: { eventGroup: EventGroup }) => {
      if (!shouldCleanEventGroupStore && !shouldCleanNextEvent) {
        return;
      }
      const nextEvents = await requestReactQuery<IEvent[]>({
        url: createQueriedURL(EVENT_SERVICE_URLS.EVENT_LIST, {
          providerId: currentProvider?.uuid,
          eventGroupId: id,
          size: 1,
          from: convertToProviderTimezone(new Date().toString()).toISOString(),
        }),
        config: { method: 'GET' },
      });
      if (!nextEvents.length) {
        shouldCleanEventGroupStore &&
          dispatch({
            type: REQUEST_ACTIONS.DELETE_EVENTGROUP,
            name: REQUEST_ACTIONS.DELETE_EVENTGROUP,
            status: STATUS.SUCCESS,
            payload: { id },
          });
        shouldCleanNextEvent &&
          dispatch({
            type: REQUEST_ACTIONS.DELETE_NEXT_EVENT_BY_GROUP_ID,
            name: REQUEST_ACTIONS.DELETE_NEXT_EVENT_BY_GROUP_ID,
            status: STATUS.SUCCESS,
            payload: { id },
          });
      }
    },
    {
      onError: (errorObj: IErrorResponse) => {
        const message = extractErrorMessage(errorObj) as any;
        showToast(
          'Get next event error',
          <Trans i18nKey={message?.key} defaults={message?.defaultMessage || message} />,
          ToastVariant.ERROR,
        );
      },
    },
  );
  const onCloseEndEventGroupModal = useCallback(() => setEndEventGroupOptions(), [
    setEndEventGroupOptions,
  ]);
  const onConfirmEndEventGroup = (options: IEndEventGroupConfirmationOptions) => {
    if (endEventGroupOptions) {
      endEventGroup.mutate(
        { eventGroup: endEventGroupOptions, options },
        {
          onSuccess: () => updateEventGroupStore.mutate({ eventGroup: endEventGroupOptions }),
        },
      );
    }
    onCloseEndEventGroupModal();
  };

  return {
    endEventGroupOptions,
    onConfirmEndEventGroup,
    onOpenEndEventGroupModal: setEndEventGroupOptions,
    onCloseEndEventGroupModal,
  };
};
